import type { BoxProps } from '@talos/kyoko';
import {
  Box,
  CurrentDocumentProvider,
  DROPDOWN_PORTAL_ID,
  getLoadingAnimation,
  MODAL_PORTAL_ID,
  POPOVER_PORTAL_ID,
  PortalContextProvider,
  useIsIntersecting,
  usePortal,
} from '@talos/kyoko';
import { useMemo, useRef } from 'react';
import styled from 'styled-components';
import type { LayoutPanelApi } from '../../AppLayout/types';
import { ErrorBoundary } from '../../ErrorBoundary';
import { PopoutStyleProvider } from '../../PopoutWindow/PopoutStyleProvider';
import { useDockviewPopoutWindow } from '../hooks/useDockviewPopoutWindow';
import { useLayoutContext } from '../providers/LayoutContextProvider';
import { PanelContextProvider } from '../providers/PanelContextProvider';
import { DockViewStyles } from '../styles';

interface PanelProps extends BoxProps {
  panelApi: LayoutPanelApi;
}

export function Panel(props: PanelProps) {
  const { panelApi } = props;
  const { hiddenPanelIds } = useLayoutContext();
  const popoutWindow = useDockviewPopoutWindow(panelApi);
  const isPopout = popoutWindow != null;
  const currentWindow = popoutWindow ?? window;
  const currentDocument = currentWindow.document;

  const panelContext = useMemo(() => {
    return {
      isPopout,
    };
  }, [isPopout]);

  if (hiddenPanelIds.has(panelApi.id)) {
    return null;
  }

  return (
    <ErrorBoundary background="backgroundContent">
      <PanelContextProvider value={panelContext}>
        <CurrentDocumentProvider currentDocument={currentDocument} currentWindow={currentWindow}>
          <PortalContextProvider>
            {/* One of four Dockview rendering outlets, requiring to be wrapped with a StyleSheetManager implementation. */}
            <PopoutStyleProvider
              includeGlobalStyles // We are the "main" dockview popout rendering outlet, so we are in charge of including the global styles (will be done if we are a popout)
              additionalGlobalStyles={isPopout ? <DockViewStyles /> : null}
              popoutWindow={isPopout ? currentWindow : null}
            >
              <InnerPanel {...props} />
            </PopoutStyleProvider>
          </PortalContextProvider>
        </CurrentDocumentProvider>
      </PanelContextProvider>
    </ErrorBoundary>
  );
}

/** Wrapper component to make sure only visible content is rendered */
function InnerPanel({ children, ...props }: PanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const isIntersecting = useIsIntersecting(panelRef);
  const { setPortalRef: modalPortalRef } = usePortal(MODAL_PORTAL_ID);
  const { setPortalRef: dropdownPortalRef } = usePortal(DROPDOWN_PORTAL_ID);
  const { setPortalRef: popoverPortalRef } = usePortal(POPOVER_PORTAL_ID);
  const isPopout = props.panelApi.location.type === 'popout';

  return (
    <Box data-testid="layout-panel" ref={panelRef} h="100%" background="backgroundContent" overflow="hidden" {...props}>
      {isPopout && (
        <>
          <Box ref={modalPortalRef} />
          <Box ref={popoverPortalRef} />
          <Box ref={dropdownPortalRef} />
        </>
      )}
      {panelRef.current && (isIntersecting ? children : <LoadingPanel datatest-id="loading-panel" />)}
    </Box>
  );
}

const LoadingPanel = styled(Box)`
  ${({ theme }) => getLoadingAnimation(theme, true, 'backgroundContent', 'gray.010')}
  height: 100%;
  width: 100%;
`;
